import React, { useState, useEffect, useCallback } from 'react';
import { Card, Switch, Button, Space, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { PrinterOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import ToothSVG from './ToothSVG';
import ToothDetailModal from './ToothDetailModal';
import DentalChartLegend from './DentalChartLegend';
import { ADULT_TEETH_LAYOUT, CHILD_TEETH_LAYOUT, getToothMetadata } from './constants/teethData';
import './DentalChart.css';

/**
 * DentalChart Component
 * Professional interactive dental chart with SVG tooth representations
 * Supports adult (32 teeth) and child (20 teeth) dentition
 * Features: multi-select, tooth history, RTL support, accessibility
 */
const DentalChart = ({
    patientId,
    treatments = [],
    onToothClick,
    selectionMode = false,
    selectedTeeth = [],
    onSelectionChange,
    readOnly = false,
    viewMode = 'adult', // 'adult' or 'child'
    showLabels = true,
    showLegend = true,
    onPrint
}) => {
    const { t } = useTranslation(['tooth', 'patient', 'common']);
    const [internalSelectedTeeth, setInternalSelectedTeeth] = useState(selectedTeeth);
    const [hoveredTooth, setHoveredTooth] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [selectedToothForDetail, setSelectedToothForDetail] = useState(null);
    const [currentViewMode, setCurrentViewMode] = useState(viewMode);

    useEffect(() => {
        setInternalSelectedTeeth(selectedTeeth);
    }, [selectedTeeth]);

    useEffect(() => {
        setInternalSelectedTeeth(selectedTeeth);
    }, [selectedTeeth]);

    /**
     * Get tooth condition based on treatment history
     * Handles ToothHistory records (one record per tooth with currentCondition)
     */
    const getToothCondition = useCallback((toothNumber) => {
        // Find the tooth history record for this specific tooth
        const toothHistory = treatments.find(t => t.toothNumber === toothNumber);

        if (!toothHistory) return 'healthy';

        // Return the currentCondition from the tooth history
        return toothHistory.currentCondition?.toLowerCase() || 'healthy';
    }, [treatments]);

    /**
     * Get tooth treatment history
     * Handles ToothHistory records (each tooth has treatments array)
     */
    const getToothHistory = useCallback((toothNumber) => {
        const toothHistory = treatments.find(t => t.toothNumber === toothNumber);

        if (!toothHistory || !toothHistory.treatments) return [];

        return toothHistory.treatments
            .map(t => ({
                date: t.date,
                procedure: t.procedure,
                condition: t.condition?.toLowerCase(),
                notes: t.notes,
                dentist: t.dentistId
            }))
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [treatments]);

    /**
     * Handle tooth selection (for treatment forms)
     */
    const handleToothSelection = useCallback((toothNumber) => {
        if (readOnly) return;

        const isSelected = internalSelectedTeeth.some(t => t.toothNumber === toothNumber || t === toothNumber);
        let newSelection;

        if (isSelected) {
            newSelection = internalSelectedTeeth.filter(t =>
                (t.toothNumber !== toothNumber && t !== toothNumber)
            );
        } else {
            const metadata = getToothMetadata(toothNumber);
            const position = metadata.name; // Full name like "Upper Right Central Incisor"
            newSelection = [...internalSelectedTeeth, { toothNumber, position }];
        }

        setInternalSelectedTeeth(newSelection);
        if (onSelectionChange) {
            onSelectionChange(newSelection);
        }
    }, [internalSelectedTeeth, onSelectionChange, readOnly]);

    /**
     * Handle tooth click (view detail or select)
     */
    const handleToothClick = useCallback((toothNumber) => {
        if (selectionMode) {
            // In selection mode, just toggle selection
            handleToothSelection(toothNumber);
        } else {
            // In view mode, show detail modal
            setSelectedToothForDetail(toothNumber);
            setDetailModalVisible(true);

            if (onToothClick) {
                onToothClick(toothNumber);
            }
        }
    }, [selectionMode, handleToothSelection, onToothClick]);

    /**
     * Get tooth position quadrant
     */
    const getToothPosition = (toothNumber) => {
        if (toothNumber >= 11 && toothNumber <= 18) return 'UR';
        if (toothNumber >= 21 && toothNumber <= 28) return 'UL';
        if (toothNumber >= 31 && toothNumber <= 38) return 'LL';
        if (toothNumber >= 41 && toothNumber <= 48) return 'LR';
        if (toothNumber >= 51 && toothNumber <= 55) return 'UR';
        if (toothNumber >= 61 && toothNumber <= 65) return 'UL';
        if (toothNumber >= 71 && toothNumber <= 75) return 'LL';
        if (toothNumber >= 81 && toothNumber <= 85) return 'LR';
        return 'UR';
    };

    /**
     * Handle print
     */
    const handlePrint = () => {
        if (onPrint) {
            onPrint();
        } else {
            window.print();
        }
    };

    /**
     * Get teeth layout based on view mode
     */
    const teethLayout = currentViewMode === 'adult' ? ADULT_TEETH_LAYOUT : CHILD_TEETH_LAYOUT;

    /**
     * Check if tooth is selected
     */
    const isToothSelected = (toothNumber) => {
        return internalSelectedTeeth.some(t =>
            t.toothNumber === toothNumber || t === toothNumber
        );
    };

    return (
        <Card
            title={
                <div className="dental-chart-header">
                    <span>{t('patient:dentalChart')}</span>
                    <Space>
                        {currentViewMode === 'adult' && (
                            <span className="view-mode-label">
                                {t('tooth:adultTeeth')} (32)
                            </span>
                        )}
                        {currentViewMode === 'child' && (
                            <span className="view-mode-label">
                                {t('tooth:childTeeth')} (20)
                            </span>
                        )}
                    </Space>
                </div>
            }
            extra={
                <Space>
                    {!selectionMode && (
                        <Switch
                            checkedChildren={t('tooth:adult')}
                            unCheckedChildren={t('tooth:child')}
                            checked={currentViewMode === 'adult'}
                            onChange={(checked) => setCurrentViewMode(checked ? 'adult' : 'child')}
                        />
                    )}
                    {!readOnly && !selectionMode && (
                        <Button
                            icon={<PrinterOutlined />}
                            onClick={handlePrint}
                        >
                            {t('common:print')}
                        </Button>
                    )}
                </Space>
            }
            className="dental-chart-card"
        >
            {/* Legend */}
            {showLegend && (
                <DentalChartLegend compact={selectionMode} showTitle={!selectionMode} />
            )}

            {selectionMode && (
                <div className="selection-mode-banner">
                    <EditOutlined /> {t('tooth:selectionModeActive', 'Click teeth to select for treatment')}
                </div>
            )}

            {/* Dental Chart */}
            <div className="dental-chart-svg-container">
                {/* Upper Jaw */}
                <div className="jaw upper-jaw-svg">
                    <div className="jaw-label">{t('tooth:positions.upper')}</div>
                    <div className="teeth-row">
                        {teethLayout.upper.map((toothNumber) => (
                            <ToothSVG
                                key={toothNumber}
                                toothNumber={toothNumber}
                                condition={getToothCondition(toothNumber)}
                                isSelected={isToothSelected(toothNumber)}
                                isHovered={hoveredTooth === toothNumber}
                                onClick={handleToothClick}
                                onMouseEnter={setHoveredTooth}
                                onMouseLeave={() => setHoveredTooth(null)}
                                readOnly={readOnly}
                                showLabel={showLabels}
                            />
                        ))}
                    </div>
                </div>

                <Divider className="jaw-divider" />

                {/* Lower Jaw */}
                <div className="jaw lower-jaw-svg">
                    <div className="teeth-row">
                        {teethLayout.lower.map((toothNumber) => (
                            <ToothSVG
                                key={toothNumber}
                                toothNumber={toothNumber}
                                condition={getToothCondition(toothNumber)}
                                isSelected={isToothSelected(toothNumber)}
                                isHovered={hoveredTooth === toothNumber}
                                onClick={handleToothClick}
                                onMouseEnter={setHoveredTooth}
                                onMouseLeave={() => setHoveredTooth(null)}
                                readOnly={readOnly}
                                showLabel={showLabels}
                            />
                        ))}
                    </div>
                    <div className="jaw-label">{t('tooth:positions.lower')}</div>
                </div>
            </div>

            {/* Selection Summary */}
            {selectionMode && internalSelectedTeeth.length > 0 && (
                <div className="selection-summary">
                    <strong>{t('tooth:selectedTeeth')}:</strong>{' '}
                    {internalSelectedTeeth.map(t => t.toothNumber || t).join(', ')}
                    {' '}({internalSelectedTeeth.length} {t('tooth:teeth')})
                </div>
            )}

            {/* Tooth Detail Modal */}
            <ToothDetailModal
                visible={detailModalVisible}
                onClose={() => setDetailModalVisible(false)}
                toothNumber={selectedToothForDetail}
                toothCondition={selectedToothForDetail ? getToothCondition(selectedToothForDetail) : 'healthy'}
                treatmentHistory={selectedToothForDetail ? getToothHistory(selectedToothForDetail) : []}
            />
        </Card>
    );
};

export default DentalChart;
